import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, concatMap, map, subscribeOn, switchMap, take, tap } from 'rxjs/operators';
import { environment } from '@environments/environment';
import { CrudOperations } from '@app/shared/helpers/interfaces/crud-operations';
import { TipoPaginadorEnum } from '@appNeo/neoShared/helpers/enums/TipoPaginador.enum';
import { Paginator } from '@app/shared/models/paginator/paginator';
import { OportunidadEstadoActual, OportunidadInterface, RequestPatchEstadoOportunidad, RequestPatchOportunidad, RequestPostOportunidad } from '@app/admin/pages/oportunidades/helpers/interfaces/oportunidad-interface';
import { Oportunidad } from '@app/admin/pages/oportunidades/helpers/entities/oportunidad/oportunidad';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { RespApi } from '@app/shared/helpers/interfaces/resp-api';
import { OportunidadesFiltrosInterface } from '@app/core/interfaces/filtros/oportunidades-filtros-interface';
import { EstadosOportunidad, EstadosOportunidadNombre } from '@app/admin/pages/oportunidades/helpers/enums/estados-oportunidad.enum';
import { ComentarioInterface } from '@app/core/interfaces/comentario-interface';
import { EmpleadosService } from '@app/core/services/empleados/empleados.service';
import { ModosVistaOportunidad } from '@app/admin/pages/oportunidades/helpers/enums/modos-vista-oportunidad.enum';
import { LocalstorageService } from '@app/core/services/localstorage/localstorage.service';
import { AuxiliarService } from '@app/shared/services/auxiliar/auxiliar.service';
import { PropuestaInterface } from '@app/admin/pages/oportunidades/helpers/interfaces/propuesta-interface';
import { PropuestasService } from '@app/core/services/propuestas/propuestas.service';
import { DatePipe } from '@angular/common';
import { Comentario } from '@app/core/entities/comentario/comentario';
import { IRequestPostTareaReunionRequerimientos, ITarea, ITareaConfiguracionReunionRequerimientos } from '@app/core/interfaces/ITarea';
import { DocumentoInterface } from '@app/admin/pages/oportunidades/helpers/interfaces/documento-interface';
import { TareasService } from '@app/core/services/tareas/tareas.service';
import { EstadosTareaEnum } from '@app/core/helpers/enums/estados-tarea.enum';
// import { AuthService } from '@app/core/services/auth/auth.service';
import { AuthService } from '@appNeo/neoCore/services/auth/auth.service';
import { DISABLE_DELAY_LOADER, LOADER_TRANSPARENT } from '@app/neo/neoCore/interceptors/loaderInterceptor/loader.interceptor';
import { IRespApi } from '@app/neo/neoShared/helpers/interfaces/IResp-api';
import { isArray } from '@datorama/akita';
import { StorageService } from '@app/neo/neoCore/services/storage/storage.service';
import { StorageTipo } from '@app/neo/neoCore/helpers/enums/storage-tipo.enum';

@Injectable({
  providedIn: 'root'
})
export class OportunidadesService implements CrudOperations {
  contextLoaderTransparent = { context: new HttpContext().set(LOADER_TRANSPARENT, true) };
  contextLoaderDisabledDelay = { context: new HttpContext().set(DISABLE_DELAY_LOADER, true) };

  public editando$ = new BehaviorSubject<boolean>(false);
  public editandoProyecto$ = new BehaviorSubject<boolean>(false);
  public editandoResponsablesProyecto$ = new BehaviorSubject<boolean>(false);
  public editandoResponsable$ = new BehaviorSubject<boolean>(false);
  public editandoContactoResponsable$ = new BehaviorSubject<boolean>(false);
  public editandoCuenta$ = new BehaviorSubject<boolean>(false);
  public editandoDatosBanco$ = new BehaviorSubject<boolean>(false);
  public editandoContactoCuenta$ = new BehaviorSubject<boolean>(false);
  public editandoContactoAdministrativo$ = new BehaviorSubject<boolean>(false);


  set editandoProyecto(flag: boolean) {
    this.editandoProyecto$.next(flag);
  }

  set editandoResponsablesProyecto(flag: boolean) {
    this.editandoResponsablesProyecto$.next(flag);
  }

  set editandoResponsable(flag: boolean) {
    this.editandoResponsable$.next(flag);
  }

  set editandoContactoResponsable(flag: boolean) {
    this.editandoContactoResponsable$.next(flag);
  }

  set editandoCuenta(flag: boolean) {
    this.editandoCuenta$.next(flag);
  }

  set editandoDatosBanco(flag: boolean) {
    this.editandoDatosBanco$.next(flag);
  }

  set editandoContactoCuenta(flag: boolean) {
    this.editandoContactoCuenta$.next(flag);
  }

  set editandoContactoAdministrativo(flag: boolean) {
    this.editandoContactoAdministrativo$.next(flag);
  }


  urlOportunidades = environment.api_base_url + '/oportunidades';
  urlOportunidadesEstados = environment.api_base_url + '/oportunidadEstados';
  urlTextos = environment.api_base_url + '/textos';
  urlFicheros = environment.api_base_url + '/ficheros';
  urlCuentas = environment.api_base_url + '/cuentas';
  nombreEntidad = 'oportunidad';
  urlSingleOportunity = environment.api_base_url + '/oportunidad';

  // Emit valor sólo con next, sin devolver el último valor con behaviourSubject al subs
  public error422Api$: Subject<RespApi> = new Subject<RespApi>();
  public oportunidad$: BehaviorSubject<Oportunidad> = new BehaviorSubject<Oportunidad>(null);
  public totalOportunidades$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public modoVistaOportunidades$: BehaviorSubject<ModosVistaOportunidad>;

  public comentarios$: BehaviorSubject<Comentario[]> = new BehaviorSubject<Comentario[]>([]);

  public columnLeftDragDrop$: BehaviorSubject<object[]> = new BehaviorSubject<object[]>([]);
  public columnRightDragDrop$: BehaviorSubject<object[]> = new BehaviorSubject<object[]>([]);

  constructor(
    private http: HttpClient,
    private empleadoService: EmpleadosService,
    private storage: StorageService,
    private auxiliar: AuxiliarService,
    private propuestaService: PropuestasService,
    private tareasService: TareasService,
    private datepipe: DatePipe,
    private authService: AuthService
  ) {
    this.iniciarModoVista();
  }

  set editando(flag: boolean) {
    this.editando$.next(flag);
  }


  get EstadosOportunidad() {
    return EstadosOportunidad;
  }

  private iniciarModoVista() {
    let modoVistaOportunidad: ModosVistaOportunidad = ModosVistaOportunidad.Listado;
    if (this.storage.get('modoVistaOportunidad', StorageTipo.SessionStorage)) {
      modoVistaOportunidad = (this.storage.get('modoVistaOportunidad', StorageTipo.SessionStorage) === 'embudo') ? ModosVistaOportunidad.Embudo : ModosVistaOportunidad.Listado;
    }
    this.modoVistaOportunidades$ = new BehaviorSubject<ModosVistaOportunidad>(modoVistaOportunidad);
    this.modoVistaOportunidades$.next(modoVistaOportunidad);
  }

  // uso para mejora tiempo evitando el mapeo en casos innecesarios como en listado oportunidades
  setOportunidad(oportunidad: Oportunidad, mapeada: boolean = false) {
    if (oportunidad !== null) {
      this.getPropuestaActiva(oportunidad.id).subscribe(propuesta => {
        propuesta.puedoEditarTipoAndNombreProyecto = this.auxiliar.getBoolean(propuesta.puedoEditarTipoAndNombreProyecto);
        if (mapeada) {
          this.propuestaService.propuesta = propuesta;
        } else {
          this.propuestaService.setPropuesta(propuesta, false);
        }
        this.oportunidad$.next(oportunidad);
      });
    } else {
      this.propuestaService.propuesta = null;
    }
  }

  set oportunidad(oportunidad: Oportunidad) {
    this.oportunidad$.next(oportunidad);
    // if (oportunidad !== null) {
    //   this.getTareasEmpleadoRegistrado(oportunidad.id).subscribe(data => {
    //     let totalTareasActivas = 0;
    //     data.forEach(element => {
    //       totalTareasActivas = (element.tareaEstadoActual.id === EstadosTareaEnum.Activa) ? totalTareasActivas + 1 : totalTareasActivas;
    //     });
    //     oportunidad.totalTareasActivas = totalTareasActivas;
    //     this.getPropuestaActiva(oportunidad.id).subscribe(propuesta => {
    //       this.propuestaService.propuesta = propuesta;
    //       this.oportunidad$.next(oportunidad);
    //     });
    //   })
    // } else {
    //   this.propuestaService.propuesta = null;
    // }
  }


  entidadesRelacionadas(oportunidad: Oportunidad) {
    let peticion = {
      totalTareasActivasEmpleado: this.getTareasEmpleadoRegistrado(oportunidad.id, undefined, undefined, { idTareaEstado: EstadosTareaEnum.Activa, idOportunidad: oportunidad.id }).pipe(
        map(data => { return data.filter(item => item.idTareaEstado == EstadosTareaEnum.Activa) }),
        catchError(err => of(null))
      ),
      propuestaActiva: this.getPropuestaActiva(oportunidad.id).pipe(
        // tap(propuesta =>  this.propuestaService.propuesta = propuesta),
        catchError(err => of(null))
      ),
    };
    return forkJoin(peticion);
  }

  vaciarOportunidad() {
    this.oportunidad$.next(null);
    this.propuestaService.vaciarPropuesta();
  }

  set modoVistaOportunidades(modo: ModosVistaOportunidad) {
    this.storage.set('modoVistaOportunidad', modo, StorageTipo.SessionStorage);
    this.modoVistaOportunidades$.next(modo);
  }

  set comentarios(comentarios: Comentario[]) {
    this.comentarios$.next(comentarios);
  }



  // persistencia con localestorage
  setInitialValuesDragDrop() {
    // this.columnLeftDragDrop$.next([]]);
    // this.columnRightDragDrop$.next([]);
  }

  set columnLeftDragDrop(data) {
  }

  set columnRightDragDrop(data) {
  }

  getAll(paginatorType?: TipoPaginadorEnum, paginator?: Paginator, filtros?: OportunidadesFiltrosInterface): Observable<object[]> {
    if (paginatorType === TipoPaginadorEnum.offline) {
      return this.http.get(`${this.urlOportunidades}?${this.auxiliar.extraerFiltros(filtros)}`, {})
        .pipe(
          tap((data: RespApi) => {
            // console.log('Data: ', data);
            this.totalOportunidades$.next(data.total);
          }),
          map((data: RespApi) => this.mapearOportunidades(data['data'])),
          catchError((err: RespApi) => {
            return this.handleError(err, 'GetAllOportunidades');
          })
        );
    } else {
      let params = `numeroElementosPaginador=`;
      if (paginator?._numeroElementosPagina !== undefined) {
        params = `numeroElementosPaginador=${paginator?._numeroElementosPagina}`;
        params += `&pagina=${paginator?._pagina + 1}`;
      }
      if (!filtros.orden) {
        filtros = { ...filtros, orden: 'identificador', tipoOrden: 'desc' };
      }
      // console.log(`${this.urlOportunidades}?${params}${this.auxiliar.extraerFiltros(filtros)}`);
      return this.http.get(`${this.urlOportunidades}?${params}${this.auxiliar.extraerFiltros(filtros)}`, { ...this.contextLoaderTransparent }).pipe(
        tap((data: RespApi) => {
          // console.log('Data: ', data);
          this.totalOportunidades$.next(data.total);
        }),
        map((data: RespApi) => this.mapearOportunidades(data['data'])),
        catchError((err: RespApi) => {
          return this.handleError(err, 'GetAllOportunidades');
        })
      );
    }
  }

  mapearOportunidades(oportunidades: OportunidadInterface[]) {
    if (oportunidades.length) {
      oportunidades.forEach((element, index) => {
        oportunidades[index].idOportunidadEstado = element?.oportunidadEstadoActual?.id;
        if (!this.propuestaService?.estadosPropuestas?.length) {
          this.propuestaService.cargarEstados();
        }
        let estadoPropuesta = this.propuestaService?.estadosPropuestas?.find(item => item.id === element?.idPropuestaEstadoActual);
        oportunidades[index].nombre = element?.nombreOportunidad;
        oportunidades[index].esProspecto = this.auxiliar.getBoolean(element?.esProspecto);

        oportunidades[index].rutaAbsolutaFotoEmpleadoResponsableComercial = (element['imagenesEmpleadoResponsableComercial']['imagenListado']) ? element['imagenesEmpleadoResponsableComercial']['imagenListado'] : element['imagenesEmpleadoResponsableComercial']['imagen'];
        oportunidades[index].rutaAbsolutaFotoEmpleadoResponsableTecnico = (element['imagenesEmpleadoResponsableTecnico']['imagenListado']) ? element['imagenesEmpleadoResponsableTecnico']['imagenListado'] : element['imagenesEmpleadoResponsableTecnico']['imagen'];
        oportunidades[index].rutaAbsolutaFotoEmpleadoJefeProyecto = (element['imagenesEmpleadoJefeProyecto']['imagenListado']) ? element['imagenesEmpleadoJefeProyecto']['imagenListado'] : element['imagenesEmpleadoJefeProyecto']['imagen'];
        oportunidades[index].estadoPropuesta = estadoPropuesta;
        // oportunidades[index].totalTareasActivas = (element?.totalTareasActivas) ? element.totalTareasActivas: 0;

      });
    }
    return oportunidades;
  }

  get(id): Observable<OportunidadInterface> {
    return this.http.get(`${this.urlOportunidades}/${id}`).pipe(
      map((data: RespApi) => {
        let oportunidad: OportunidadInterface = data['data'];
        oportunidad.esProspecto = this.auxiliar.getBoolean(oportunidad.esProspecto);
        return oportunidad;
      })
    );
  }

  getEmpleadosDefecto() {
    return this.http.get(`${this.urlOportunidades}/oportunidadEmpleadosDefecto`, {}).pipe(
      // tap(data => console.log('Data Tipo imagen: ', data)),
      map((data: IRespApi) => data['data']));
  }
  descargarExcel(filtros?): Observable<string> {
    let url = this.urlOportunidades + '/excel';
    let request = `${url}?${this.auxiliar.extraerFiltros(filtros)}`;
    return this.http.get(request, { ...this.contextLoaderTransparent }).pipe(
      map((data: IRespApi) => data['data']),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'RequestDescargaOportunidadesExcel');
      })
    );
  }

  create(request: RequestPostOportunidad): Observable<any> {
    console.log('Creacion oportunidad ', request);
    if (request?.idProyectoItemModalidad == '') {
      delete request.idProyectoItemModalidad;
    }
    return this.http.post<RespApi>(`${this.urlOportunidades}`, request, { ...this.contextLoaderDisabledDelay }).pipe(
      map((resp: RespApi) => {
        // console.log(`Post ok oportunidad ${ resp }`);
        return resp.data;
      }),
      catchError((err: RespApi) => {
        return this.handleError(err, 'RequestPostOportunidad');
      })
    );
  }


  update(id: any, request?: OportunidadInterface | RequestPatchOportunidad): Observable<any> {
    console.log('OPORTUNIDAD update ', request, `${this.urlOportunidades}/${id}`);
    return this.http.patch(`${this.urlOportunidades}/${id}`, request).pipe(
      tap(data => console.log('Data update oportunidad: ', data)),
      map((data: OportunidadInterface[]) => data['data']),
      catchError((err: RespApi) => {
        console.log('Error');
        return this.handleError(err);
      })
    );
  }

  updateEstado(id: string, request: RequestPatchEstadoOportunidad) {
    return this.http.post(`${this.urlOportunidades}/${id}/establecerEstado`, request).pipe(
      // tap(data => console.log('Data update oportunidad: ', data)),
      map((data: OportunidadInterface[]) => data['data']),
      catchError((err: RespApi) => {
        return this.handleError(err, 'RequestPatchEstadoOportunidad');
      })
    );
  }

  deleteLogic(id: string, request: RequestPatchEstadoOportunidad): Observable<any> {
    return this.http.patch(`${this.urlOportunidades}/${id}`, request).pipe(
      // tap(data => console.log('Data: ', data)),
      map((data: any[]) => data['data']));
  }

  delete(id: string): Observable<any> {
    return this.http.delete(`${this.urlOportunidades}/${id}`).pipe(
      map((data: any[]) => !data['error']),
      catchError((err: RespApi) => {
        return this.handleError(err, 'RequestDeleteOportunidad');
      })
    );



  }

  getEstados(): Observable<OportunidadEstadoActual[]> {
    return this.http.get(`${this.urlOportunidadesEstados}`).pipe(
      map((data: RespApi) => data['data'])
    );
  }

  /********************************/
  /* Funciones complementarias mantenimiento oportunidad */
  /********************************/
  sendSolicitudDatos(id: string, request: any) {
    return this.http.post(`${this.urlOportunidades}/${id}/enviarSolicitudDatos`, request).pipe(
      map((data) => !data['error']),
      catchError((err: RespApi) => {
        return this.handleError(err, 'RequestPostSolicitudDatos');
      })
    );
  }

  /********************************/
  /* CONTACTOS CUENTA OPORTUNIDAD  */
  /********************************/
  getContactosCuenta(idCuenta): Observable<Comentario[]> {
    let filtros = { orden: 'nombre', tipoOrden: 'asc' };
    return this.http.get(`${this.urlCuentas}/${idCuenta}/contactos?${this.auxiliar.extraerFiltros(filtros)}`).pipe(
      // tap( data => console.log('info comentarios ', data)),
      map((data: RespApi) => data['data'])
    );

  }

  /********************************/
  /* COMENTARIOS                  */
  /********************************/
  getComentarios(oportunidad): Observable<Comentario[]> {
    let filtros = { orden: 'id', tipoOrden: 'asc' };
    return this.http.get(`${this.urlOportunidades}/${oportunidad.id}/comentarios?${this.auxiliar.extraerFiltros(filtros)}`).pipe(
      // tap( data => console.log('info comentarios ', data)),
      map((data: RespApi) => this.mapearComentario(data.data, oportunidad.id))
    );

  }

  mapearComentario(comentarios: Comentario[], idObjeto: string): Comentario[] {
    comentarios.map(object => Object.assign(object, {
      idObjeto
    }));
    return comentarios;
  }

  createComentario(comentario: ComentarioInterface): Observable<any> {

    if (comentario) {
      const objComentario = Object.assign({ texto: comentario.contenido });
      return this.http.post(`${this.urlOportunidades}/${comentario.idObjeto}/comentarios`, objComentario).pipe(
        map((data: RespApi) => data),
        catchError((err: RespApi) => {
          return this.handleError(err);
        })
      );
    } else {
      return throwError(new Error('Error'));
    }

  }

  updateComentario(comentario: ComentarioInterface): Observable<any> {
    const objComentario = Object.assign({ texto: comentario.contenido });
    return this.http.patch(`${this.urlTextos}/${comentario.id}`, objComentario).pipe(
      map((data: ComentarioInterface[]) => data['data']),
      catchError((err: RespApi) => {
        return this.handleError(err);
      })
    );
  }

  deleteComentario(comentario: ComentarioInterface): Observable<any> {
    if (comentario) {
      return this.http.delete(`${this.urlTextos}/${comentario.id}`).pipe(
        catchError((err: RespApi) => {
          return this.handleError(err);
        })
      );
    } else {
      return throwError(new Error('Error'));
    }
  }

  /**Ficheros */

  getFicheros(id: string) {
    console.log('[Ficheros llamada]');
    return this.http.get(`${this.urlOportunidades}/${id}/ficheros?clave=fichero`).pipe(
      map((data: RespApi) => data['data']));
  }

  getFichero(id: string, idFichero: string) {
    console.log('[Ficheros llamada]');
    return this.http.get(`${this.urlOportunidades}/${id}/ficheros/${idFichero}`).pipe(
      map((data: RespApi) => data['data']));
  }

  subirFicheros(idEntidad: string, request: any) {
    console.log('Subir ficheros ', idEntidad, request);
    return this.http.post<IRespApi>(`${this.urlOportunidades}/${idEntidad}/ficheros`, request).pipe(
      map((resp: IRespApi) => resp.data),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestPost' + this.nombreEntidad + 'Fichero');
      })
    );
  }
  editarFichero(idFichero: string, request: any) {
    return this.http.patch<IRespApi>(`${this.urlFicheros}/${idFichero}/ficheros`, request).pipe(
      map((resp: IRespApi) => resp.data),
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestEditar' + this.nombreEntidad + 'Fichero');
      })
    );
  }

  eliminarFichero(id: string, idFichero?: string) {

    if (id) {
      let peticion = `${this.urlOportunidades}/${id}/ficheros`;
      if (idFichero) {
        peticion = `${peticion}/${idFichero}`;
      }
      return this.http.delete(peticion).pipe(
        map((data: IRespApi) => data),
        catchError((err: RespApi) => {
          return this.handleError(err, 'IRequestDelete' + this.nombreEntidad + 'Fichero');
        })
      );
    } else {
      return throwError(new Error('Error intentando eliminar fichero sin especificar id'));
    }
  }

  subirImagenTemporal(imagen: File | FileList, camposExtra?: object) {
    const formData: FormData = new FormData();
    Object.values((imagen)).forEach((valor, clave, imagenes) => console.log(valor, valor.name
    ));
    Object.values((imagen)).forEach((valor, clave, imagenes) => formData.append('fichero[]', valor, valor.name))

    console.log(formData);
    if (camposExtra && Object.keys(camposExtra)?.length > 0) {
      Object.entries(camposExtra).forEach(([key, value]) => {
        formData.append(key, value.toString());
      });
    }
    return this.http.post<IRespApi>(`${this.urlOportunidades}/ficherosTemp`, formData).pipe(
      catchError((err: IRespApi) => {
        return this.handleError(err, 'IRequestPostImagenTempExtra');
      })
    );
  }



  postFile(fileToUpload: File[], idObjeto: string): Observable<any> {
    const formData: FormData = new FormData();
    if (fileToUpload?.length) {
      for (let i = 0; i < fileToUpload.length; i++)
        formData.append('fichero[]', fileToUpload[i], fileToUpload[i].name);
    }
    return this.http.post(`${this.urlOportunidades}/${idObjeto}/ficheros`, formData).pipe(
      map((data: RespApi) => data),
      catchError((err: RespApi) => {
        return this.handleError(err, 'RequestSubidaFicheros');
      })
    );
  }

  configuracionFicheros(clave?: string, parametro?: string): Observable<any> {
    let paramClave = (clave) ? `?clave=${clave}` : '';
    paramClave = (clave && parametro) ? `${paramClave}&parametro=${parametro}` : paramClave;
    return this.http.get(`${this.urlOportunidades}/configuracionFicheros${paramClave}`).pipe(
      map((data: RespApi) => data['data']));
  }



  /************************************ 
   *   Propuestas    asociadas        *
  *************************************/
  //  TODO endpoint propio
  getPropuestas(id: string, paginatorType?: TipoPaginadorEnum, paginator?: Paginator, filtros?: any): Observable<PropuestaInterface[]> {
    let url = (paginatorType === TipoPaginadorEnum.online && filtros)
      ? `${this.urlOportunidades}/${id}/propuestas?${this.auxiliar.extraerFiltrosPaginacion(paginator)}${this.auxiliar.extraerFiltros(filtros)}`
      : `${this.urlOportunidades}/${id}/propuestas`;

    return this.http.get(url).pipe(
      map((data: RespApi) => {
        if (data.total > 0) {
          return this.propuestaService.mapearPropuestas(data['data']);
        } else {
          return [];
        }
      })
    );
  }


  /**
   * getPropuestas(id: string, paginatorType?: TipoPaginadorEnum, paginator?: Paginator, filtros?: any): Observable<PropuestaInterface[]> {

    return this.http.get(`${this.urlOportunidades}/${id}/propuestas?${this.auxiliar.extraerFiltrosPaginacion(paginator)}${this.auxiliar.extraerFiltros(filtros)}`).pipe(
      map((data: RespApi) =>  {
        if ( data.total > 0 ) {
          return this.propuestaService.mapearPropuestas(data['data']);
        } else {
          return [];
        }
      })
    );
  }  
   */
  //ficheros all propuestas
  getFicherosPropuestas(id: string): Observable<DocumentoInterface[]> {
    return this.getPropuestas(id).pipe(
      map(propuestas => {
        let documentos: DocumentoInterface[] = [];
        propuestas.forEach(item => {
          return this.propuestaService.getFicheros(item.id).pipe(
            map(filesPropuesta => {
              filesPropuesta.map(doc => doc.rutaCompleta = environment.base_url + doc.ruta)
              documentos.push(filesPropuesta);
              return documentos;
            })
          );
        });
        return documentos
      })
    );
  }


  getPropuestaActiva(id: string): Observable<PropuestaInterface> {
    return this.http.get(`${this.urlOportunidades}/${id}/propuestaActiva`).pipe(
      map((data: RespApi) => data['data'])
    );
  }

  nuevaPropuesta(id: string) {
    return this.http.post(`${this.urlOportunidades}/${id}/nuevaPropuesta`, {}).pipe(
      map((data: RespApi) => data),
      catchError((err: RespApi) => {
        return this.handleError(err);
      })
    );
  }


  /************************************ 
   *   TAREAS OPORTUNIDAD        *
  *************************************/
  //  TODO endpoint propio
  getTareaConfiguracionReunionRequerimientos(id: string): Observable<ITareaConfiguracionReunionRequerimientos> {
    return this.http.get(`${this.urlOportunidades}/${id}/tareaConfiguracionReunionRequerimientos`).pipe(
      map((data: RespApi) => data['data'])
    );
  }

  createTareaReunionRequerimientos(id: string, request: IRequestPostTareaReunionRequerimientos) {
    return this.http.post(`${this.urlOportunidades}/${id}/tareaReunionRequerimientos`, request).pipe(
      map((data: RespApi) => data),
      catchError((err: RespApi) => {
        return this.handleError(err, 'IRequestPostTareaReunionRequerimientos');
      })
    );
  }

  getTareasEmpleadoRegistrado(id?: string, paginatorType?: TipoPaginadorEnum, paginator?: Paginator, filtros?: any): Observable<any> {
    return this.http.get(`${this.urlOportunidades}/${id}/tareasEmpleadoRegistrado`).pipe(
      map((data: RespApi) => this.mapearTareas(data['data']))
    );
  }

  mapearTareas(tareas: any[]) {
    if (tareas.length) {
      tareas.forEach((element, index) => {
        tareas[index].fecha = new Date(element?.fechaInicio);
        tareas[index].horaDesde = new Date(element?.fechaInicio);
        tareas[index].horaHasta = new Date(element?.fechaFin);

      });
    }
    return tareas;
  }

  /********************************/
  /* TAREAS Oportunidad  */
  /********************************/
  getTareasOportunidad(id?: string, paginatorType?: TipoPaginadorEnum, paginator?: Paginator, filtros?: any): Observable<any> {
    return this.http.get(`${this.urlOportunidades}/${id}/tareasOportunidad`).pipe(
      map((data: RespApi) => this.mapearTareas(data['data']))
    );
    // return this.tareasService.getAll(paginatorType, paginator, filtros).pipe(
    //   map((tareas: ITarea[]) => tareas.filter(tarea => tarea?.idOportunidad === id))
    // );
  }

  /************************************ 
   * Funciones apoyo                  *
  *************************************/
  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<any> {
    const id = route.params.codigo;
    let peticion = {
      oportunidad: this.get(id).pipe(
        catchError(err => of(null))
      ),
      propuestaActiva: this.getPropuestaActiva(id).pipe(
        catchError(err => of(null))
      ),
    };
    return forkJoin(peticion);




    // // return this.get(id);
    // let peticionOportunidad = this.get(id);
    // let peticionPropuestaActiva = this.getPropuestaActiva(id);
    // return forkJoin([peticionOportunidad, peticionPropuestaActiva]);
  }

  /********************************/
  /* MANEJADOR ERRORES            */
  /********************************/
  private handleError(err: RespApi, idRequest = '') {
    // control error server con un html ok
    if (err == 'OK') {
      let mensaje = environment.errorServidorSinCodigo;
      return throwError({ 'error': true, 'data': mensaje, mensaje, 'err': err });
    }
    if (err == 'Unknown Error') {
      let mensaje = environment.errorServidorUnknown;
      return throwError({ 'error': true, 'data': mensaje, mensaje, 'err': err });
    }
    // dialog específico
    err.request = idRequest;
    if (err.codigoError === 422) {
      this.error422Api$.next(err);
    }

    // pantalla común
    let mensaje = err?.mensaje;
    if (err.codigoError === 403) {
      mensaje = `${err.mensaje}: `;
      err.data.forEach(element => {
        mensaje += element.mensaje;
      });
    }
    if (err.codigoError === 404) {
      let avisos = '';
      if (err.data) {
        err.data.forEach(element => {
          if (element.id === 999) {
            avisos = ' ' + element.mensaje + ' ';
          }
        });
      }
      mensaje = err?.mensaje + avisos;
    }
    return throwError({ 'error': true, 'data': err.data, mensaje, 'err': err });
  }


}